import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Divider,
  Collapse,
  LinearProgress,
  Grid
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatCurrency, formatNumber, formatDate } from '../../utils/estimationEngine';
import AssumptionsDisclaimer from './AssumptionsDisclaimer';

const ResultsSummary = ({
  results,
  onSave,
  onExportPDF,
  onCompare,
  onStartNew,
  isSaved = false,
  language = 'en'
}) => {
  const [showAssumptions, setShowAssumptions] = useState(false);

  const content = {
    en: {
      title: 'Preliminary Budget Estimation',
      generatedOn: 'Generated',
      budgetRange: 'Estimated Budget Range',
      minimum: 'Minimum',
      maximum: 'Maximum',
      average: 'Average',
      perSqft: 'per sq ft',
      breakdown: 'Cost Breakdown',
      materials: 'Estimated Material Quantities',
      approx: '±',
      viewAssumptions: 'View Assumptions & Disclaimer',
      hideAssumptions: 'Hide Assumptions',
      saveProject: 'Save Project',
      saved: 'Saved',
      exportPdf: 'Export PDF',
      compareOption: 'Compare Another Option',
      startNew: 'Start New Estimation'
    },
    my: {
      title: 'Anggaran Bajet Awal',
      generatedOn: 'Dijana',
      budgetRange: 'Julat Bajet Anggaran',
      minimum: 'Minimum',
      maximum: 'Maksimum',
      average: 'Purata',
      perSqft: 'per kaki persegi',
      breakdown: 'Pecahan Kos',
      materials: 'Anggaran Kuantiti Bahan',
      approx: '±',
      viewAssumptions: 'Lihat Andaian & Penafian',
      hideAssumptions: 'Sembunyikan Andaian',
      saveProject: 'Simpan Projek',
      saved: 'Disimpan',
      exportPdf: 'Eksport PDF',
      compareOption: 'Bandingkan Pilihan Lain',
      startNew: 'Mulakan Anggaran Baru'
    }
  };

  const t = content[language] || content.en;

  const { costs, breakdown, materials, inputs, generatedAt } = results;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 700, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          {t.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {inputs.projectName || (language === 'my' ? inputs.houseTypeLabelMy : inputs.houseTypeLabel)} • {formatNumber(inputs.builtUpArea)} sq ft
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {t.generatedOn}: {formatDate(generatedAt, language)}
        </Typography>
      </Box>

      {/* Budget Range Card */}
      <Paper
        elevation={0}
        sx={{
          border: '2px solid',
          borderColor: 'primary.main',
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3
        }}
      >
        <Box sx={{ bgcolor: 'primary.main', px: 3, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            {t.budgetRange}
          </Typography>
        </Box>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {t.minimum}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatCurrency(costs.minimum)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {t.maximum}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {formatCurrency(costs.maximum)}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                {t.average}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(costs.average)}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" color="text.secondary">
                RM {costs.perSqftMin} - {costs.perSqftMax}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {t.perSqft}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Cost Breakdown */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: 3,
          mb: 3
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t.breakdown}
        </Typography>

        {/* Structure */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {language === 'my' ? breakdown.structure.labelMy : breakdown.structure.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(breakdown.structure.amount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={breakdown.structure.percentage}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
              {breakdown.structure.percentage}%
            </Typography>
          </Box>
        </Box>

        {/* Finishes */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {language === 'my' ? breakdown.finishes.labelMy : breakdown.finishes.label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatCurrency(breakdown.finishes.amount)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={breakdown.finishes.percentage}
              sx={{
                flex: 1,
                height: 8,
                borderRadius: 4,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'secondary.main',
                  borderRadius: 4
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
              {breakdown.finishes.percentage}%
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Material Quantities */}
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          p: 3,
          mb: 3
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          {t.materials}
        </Typography>

        <Grid container spacing={1}>
          {Object.entries(materials).map(([key, material]) => (
            <Grid item xs={6} key={key}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {language === 'my' ? material.labelMy : material.label}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t.approx} {formatNumber(material.quantity)} {language === 'my' ? material.unitMy : material.unit}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Assumptions & Disclaimer Toggle */}
      <Button
        fullWidth
        variant="text"
        onClick={() => setShowAssumptions(!showAssumptions)}
        endIcon={showAssumptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        sx={{
          justifyContent: 'space-between',
          textTransform: 'none',
          color: 'text.secondary',
          mb: 2
        }}
      >
        {showAssumptions ? t.hideAssumptions : t.viewAssumptions}
      </Button>

      <Collapse in={showAssumptions}>
        <AssumptionsDisclaimer language={language} />
      </Collapse>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant={isSaved ? 'outlined' : 'contained'}
            onClick={onSave}
            disabled={isSaved}
            startIcon={isSaved ? <CheckCircleIcon /> : <SaveIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {isSaved ? t.saved : t.saveProject}
          </Button>
          <Button
            variant="outlined"
            onClick={onExportPDF}
            startIcon={<PictureAsPdfIcon />}
            sx={{
              flex: 1,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600
            }}
          >
            {t.exportPdf}
          </Button>
        </Box>

        <Button
          variant="outlined"
          onClick={onCompare}
          startIcon={<CompareArrowsIcon />}
          sx={{
            py: 1.5,
            borderRadius: 2,
            textTransform: 'none'
          }}
        >
          {t.compareOption}
        </Button>

        <Button
          variant="text"
          onClick={onStartNew}
          startIcon={<RefreshIcon />}
          sx={{
            textTransform: 'none',
            color: 'text.secondary'
          }}
        >
          {t.startNew}
        </Button>
      </Box>

      {/* Footer */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          RumahKira • {language === 'my' ? 'Untuk Tujuan Perancangan Sahaja' : 'For Planning Purposes Only'}
        </Typography>
      </Box>
    </Box>
  );
};

export default ResultsSummary;
